/**
 * Secure Logging Utility
 * 
 * Provides safe logging functions that sanitize sensitive data
 * before writing to console. Use these instead of console.log/error/warn
 * for production code.
 */

// Check if running in development mode
const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

/**
 * Redact email addresses - show first 2 chars and domain only
 * Example: "john@example.com" → "jo***@example.com"
 */
export const redactEmail = (email: string): string => {
  if (!email || typeof email !== 'string') return '[invalid-email]';
  
  const [local, domain] = email.split('@');
  if (!domain) return '[invalid-email]';
  
  const redactedLocal = local.length > 2 
    ? `${local.slice(0, 2)}***`
    : '***';
  
  return `${redactedLocal}@${domain}`;
};

/**
 * Redact tokens/secrets - show only first 8 and last 4 chars
 * Example: "sk-proj-abc123...xyz789" → "sk-proj-***xyz789"
 */
export const redactToken = (token: string): string => {
  if (!token || typeof token !== 'string') return '[invalid-token]';
  
  if (token.length < 12) return '***';
  
  const prefix = token.slice(0, 8);
  const suffix = token.slice(-4);
  
  return `${prefix}***${suffix}`;
};

/**
 * Sanitize user object - keep only safe fields
 */
export const sanitizeUser = (user: any): object => {
  if (!user) return { user: null };
  
  return {
    id: user.id || '[no-id]',
    email: user.email ? redactEmail(user.email) : '[no-email]',
    created_at: user.created_at || '[unknown]'
  };
};

/**
 * Sanitize error object - remove sensitive data from error bodies
 */
export const sanitizeError = (error: any): object => {
  if (!error) return { error: null };
  
  // For Error objects
  if (error instanceof Error) {
    return {
      message: error.message,
      name: error.name,
      // Don't include stack traces in production
      ...(isDevelopment && { stack: error.stack })
    };
  }
  
  // For plain objects (like API error responses)
  if (typeof error === 'object') {
    return {
      message: error.message || error.error || 'Unknown error',
      code: error.code || error.status,
      // Don't log full response bodies which may contain sensitive data
    };
  }
  
  return { error: String(error) };
};

/**
 * Secure logger - use instead of console.log
 * Automatically sanitizes common sensitive data patterns
 */
export const secureLog = {
  /**
   * Log general info (production safe)
   */
  info: (message: string, ...args: any[]) => {
    console.log(`[INFO] ${message}`, ...args);
  },
  
  /**
   * Log errors (sanitized)
   */
  error: (message: string, error?: any) => {
    if (error) {
      console.error(`[ERROR] ${message}`, sanitizeError(error));
    } else {
      console.error(`[ERROR] ${message}`);
    }
  },
  
  /**
   * Log warnings (sanitized)
   */
  warn: (message: string, ...args: any[]) => {
    console.warn(`[WARN] ${message}`, ...args);
  },
  
  /**
   * Debug logs - only show in development
   */
  debug: (message: string, ...args: any[]) => {
    if (isDevelopment) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  },
  
  /**
   * Log user actions (with sanitized user data)
   */
  user: (action: string, user: any) => {
    console.log(`[USER] ${action}`, sanitizeUser(user));
  },
  
  /**
   * Log email operations (redacted)
   */
  email: (action: string, email: string) => {
    console.log(`[EMAIL] ${action}`, redactEmail(email));
  },
  
  /**
   * Log token operations (redacted)
   */
  token: (action: string, token: string) => {
    if (isDevelopment) {
      // Show partial token in dev
      console.log(`[TOKEN] ${action}`, redactToken(token));
    } else {
      // Don't log tokens at all in production
      console.log(`[TOKEN] ${action} [redacted]`);
    }
  },
  
  /**
   * Log authentication events
   */
  auth: (event: string, details?: object) => {
    // Only log safe details (no passwords, tokens, etc.)
    const safeDetails = details ? {
      // Filter out sensitive keys
      ...Object.entries(details).reduce((acc, [key, value]) => {
        if (['password', 'token', 'secret', 'apikey', 'api_key'].some(s => key.toLowerCase().includes(s))) {
          return acc; // Skip sensitive fields
        }
        // Redact emails
        if (key === 'email' && typeof value === 'string') {
          acc[key] = redactEmail(value);
        } else {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>)
    } : {};
    
    console.log(`[AUTH] ${event}`, safeDetails);
  },
  
  /**
   * Log API calls (production safe)
   */
  api: (method: string, endpoint: string, status?: number) => {
    console.log(`[API] ${method} ${endpoint}`, status ? `Status: ${status}` : '');
  }
};

/**
 * Development-only logger - completely disabled in production
 */
export const devLog = {
  log: (...args: any[]) => {
    if (isDevelopment) {
      console.log('[DEV]', ...args);
    }
  },
  
  warn: (...args: any[]) => {
    if (isDevelopment) {
      console.warn('[DEV]', ...args);
    }
  },
  
  error: (...args: any[]) => {
    if (isDevelopment) {
      console.error('[DEV]', ...args);
    }
  }
};

export default secureLog;
